import { useMemo } from "react";
import { cn } from "@/dentlab/src/lib/util";
import { JobDocumentWithFiles } from "@/dentlab/src/store/Jobs/job-documents.store";
import { JobDocumentTypeEnum, mapJobDocumentTypeToColor, jobDocumentTypeToTagText, jobDocumentTypeToText } from "../../job-document/job-document.types";
import { formatDate, formatCurrency } from "@/dentlab/src/lib/utils/utils-functions";
import { JobDocumentSidebarFileItem } from "./job-document-sidebar-file-item.component";
import { getJobDocumentIcon } from "../../job-document/job-document.component";

interface JobDocumentSidebarItemProps {
	document: JobDocumentWithFiles;
	isSelected: boolean;
	onSelect: (documentId: number) => void;
	onFileClick: (fileId: string) => void;
}

export const JobDocumentSidebarItem: React.FC<JobDocumentSidebarItemProps> = ({
	document,
	isSelected,
	onSelect,
	onFileClick,
}) => {
	const docType = document.type as JobDocumentTypeEnum;
	const color = mapJobDocumentTypeToColor[docType] || "#797B84";

	const isRueckverfolgung = docType === JobDocumentTypeEnum.Tracing 
	let typeLabel = "Dokument"
	if (isRueckverfolgung) {
		typeLabel = document.title || jobDocumentTypeToText[docType];
	} else if (jobDocumentTypeToTagText[docType]) {
		typeLabel = jobDocumentTypeToTagText[docType]
	}

	const sortedFiles = useMemo(() => {
		if (!document.files_with_shares) return [];
		return [...document.files_with_shares].sort((a, b) => {
			if (!a.created_at || !b.created_at) return 0;
			return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
		});
	}, [document.files_with_shares]);

	return (
		<div className="flex flex-col">
			<button
				onClick={() => onSelect(document.id)}
				onKeyDown={(e) => {
					if (e.key === "Enter") onSelect(document.id);
				}}
				className={cn(
					"flex items-center gap-2 px-2 py-1.5 w-full text-left rounded-sm transition-colors",
					"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
					isSelected ? "bg-blue-100" : "hover:bg-gray-100"
				)}
				data-testid={`job-document-sidebar-item-${document.id}`}
				aria-label={`Dokument ${typeLabel} auswählen`}
				aria-selected={isSelected}
			>
				<span className="[&>svg]:h-4 [&>svg]:w-4 flex-shrink-0" style={{ color }}>
					{getJobDocumentIcon(docType)}
				</span>
				<span className="text-sm font-medium flex-shrink-0">{typeLabel}</span>
				{document.date && !isRueckverfolgung && (
					<span className="text-xs text-gray-500 flex-shrink-0">{formatDate(document.date)}</span>
				)}
				{document.amount !== null && document.amount !== undefined && !isRueckverfolgung && (
					<span className="text-xs text-gray-500 flex-shrink-0">{formatCurrency(document.amount)}</span>
				)}
			</button>
			{sortedFiles.length > 0 && (
				<div className="flex flex-col">
					{sortedFiles.map((file) => (
						<JobDocumentSidebarFileItem
							key={file.id}
							file={file}
							jobDocument={document}
							onOpenFile={onFileClick}
						/>
					))}
				</div>
			)}
		</div>
	);
};
